import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { sortCompare } from 'app/helpers/sortCompare';
import { ExposedListApplicationTypesManualStatisticsRes } from 'app/interfaces/responses/Exposed/exposedListApplicationTypesManualStatisticsRes';
import { ExposedListApplicationsManualStatisticsRes } from 'app/interfaces/responses/Exposed/exposedListApplicationsManualStatisticsRes';
import { ApplicationsService } from 'app/services/api/applications/applications.service';
import { TranslationService } from 'app/services/api/translation/translation.service';

@Component({
    selector: 'app-applications-manual-statistics',
    templateUrl: './applications-manual-statistics.component.html',
    styleUrls: ['./applications-manual-statistics.component.scss'],
})
export class ApplicationsManualStatisticsComponent implements OnInit {
    //#region Globals

    @ViewChild('tablePaginator') statsPaginator: MatPaginator;

    formEnabled = true;
    types: Array<ExposedListApplicationTypesManualStatisticsRes>;
    stats: MatTableDataSource<ExposedListApplicationsManualStatisticsRes> = new MatTableDataSource();
    headerColumns: string[] = [
        'date',
        'totalProjectsInStatusRegistered',
        'totalProjectsThisYear',
        'totalProjectsThisWeek',
        'totalProjectsThisWeekInStatusRegistered',
        'totalProjectsThisWeekInStatusClosed',
        'totalProjectsThisWeekMissingElectronicSignature',
        'dateOfOldestProjectInStatusRegistered',
        'period',
        'delete',
    ];

    form: UntypedFormGroup;

    //#endregion

    //#region Init

    constructor(
        private applicationsService: ApplicationsService,
        private formBuilder: UntypedFormBuilder,
        private translationService: TranslationService,
        private fuseConfirmationService: FuseConfirmationService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.fetchTypes();
    }

    ngAfterViewInit(): void {
        this.stats.paginator = this.statsPaginator;
    }

    initForm() {
        this.form = this.formBuilder.group({
            date: new UntypedFormControl(new Date(new Date().toDateString()), [Validators.required]),
            applicationTypesManualStatisticsId: new UntypedFormControl('', [Validators.required]),
            totalProjectsInStatusRegistered: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisYear: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisWeek: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisWeekInStatusRegistered: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisWeekInStatusOnHold: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisWeekInStatusClosed: new UntypedFormControl(null, [Validators.required]),
            totalProjectsThisWeekMissingElectronicSignature: new UntypedFormControl(null, [Validators.required]),
            dateOfOldestProjectInStatusRegistered: new UntypedFormControl(null, [Validators.required]),
            periodFrom: new UntypedFormControl(null, [Validators.required]),
            periodTo: new UntypedFormControl(null, [Validators.required]),
        });
    }

    //#endregion

    //#region Http

    fetchTypes() {
        this.applicationsService.listManualStatisticsTypes().subscribe((res) => {
            this.types = res;
        });
    }

    fetchStats(typeId: number) {
        this.applicationsService
            .listManualStatistics({
                ApplicationTypesManualStatisticsId: typeId,
            })
            .subscribe((res) => {
                this.stats.data = res;
            });
    }

    addStat() {
        if (!this.form.valid) {
            return;
        }

        this.form.disable();
        this.formEnabled = false;

        var typeId = this.form.get('applicationTypesManualStatisticsId').value;
        var date = this.form.get('date').value;
        var periodFrom = this.form.get('periodFrom').value;
        var periodTo = this.form.get('periodTo').value;

        this.applicationsService
            .addManualStatistic({
                date: this.form.get('date').value,
                applicationTypesManualStatisticsId: this.form.get('applicationTypesManualStatisticsId').value,
                totalProjectsInStatusRegistered: this.form.get('totalProjectsInStatusRegistered').value,
                totalProjectsThisYear: this.form.get('totalProjectsThisYear').value,
                totalProjectsThisWeek: this.form.get('totalProjectsThisWeek').value,
                totalProjectsThisWeekInStatusRegistered: this.form.get('totalProjectsThisWeekInStatusRegistered').value,
                totalProjectsThisWeekInStatusOnHold: this.form.get('totalProjectsThisWeekInStatusOnHold').value,
                totalProjectsThisWeekInStatusClosed: this.form.get('totalProjectsThisWeekInStatusClosed').value,
                totalProjectsThisWeekMissingElectronicSignature: this.form.get('totalProjectsThisWeekMissingElectronicSignature').value,
                dateOfOldestProjectInStatusRegistered: this.form.get('dateOfOldestProjectInStatusRegistered').value,
                periodFrom: this.form.get('periodFrom').value,
                periodTo: this.form.get('periodTo').value,
            })
            .subscribe((res) => {
                this.form.enable();
                this.formEnabled = true;
                if (res) {
                    this.form.reset();
                    // Make sure to reset displayed validators
                    Object.keys(this.form.controls).forEach((key) => {
                        this.form.controls[key].setErrors(null);
                    });

                    // We want these values to remain untouched after the form has
                    // been successfully submitted.
                    this.form.get('applicationTypesManualStatisticsId').setValue(typeId);
                    this.form.get('date').setValue(date);
                    this.form.get('periodFrom').setValue(periodFrom);
                    this.form.get('periodTo').setValue(periodTo);
                }
                this.fetchStats(typeId);
            });
    }

    deleteStat(id: number) {
        let confirmationForm = this.formBuilder.group({
            title: this.translationService.translate('info_confirm_delete_entry_header'),
            message: this.translationService.translate('info_confirm_delete_entry_message'),
            icon: {
                show: true,
                name: 'heroicons_solid:trash',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this.fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.applicationsService.deleteManualStatistic({ id: id }).subscribe(() => {
                    this.fetchStats(this.form.get('applicationTypesManualStatisticsId').value);
                });
            }
        });
    }

    //#endregion

    //#region General functions

    sortData(sort: Sort) {
        const data = this.stats.data.slice();
        if (!sort.active || sort.direction === '') {
            this.stats.data = data;
            return;
        }

        this.stats.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'date':
                    return sortCompare(a.date, b.date, isAsc);
                case 'totalProjectsInStatusRegistered':
                    return sortCompare(a.totalProjectsInStatusRegistered, b.totalProjectsInStatusRegistered, isAsc);
                case 'totalProjectsThisWeek':
                    return sortCompare(a.totalProjectsThisWeek, b.totalProjectsThisWeek, isAsc);
                case 'totalProjectsThisWeekInStatusRegistered':
                    return sortCompare(a.totalProjectsThisWeekInStatusRegistered, b.totalProjectsThisWeekInStatusRegistered, isAsc);
                case 'totalProjectsThisWeekInStatusClosed':
                    return sortCompare(a.totalProjectsThisWeekInStatusClosed, b.totalProjectsThisWeekInStatusClosed, isAsc);
                case 'totalProjectsThisWeekMissingElectronicSignature':
                    return sortCompare(
                        a.totalProjectsThisWeekMissingElectronicSignature,
                        b.totalProjectsThisWeekMissingElectronicSignature,
                        isAsc
                    );
                case 'dateOfOldestProjectInStatusRegistered':
                    return sortCompare(a.dateOfOldestProjectInStatusRegistered, b.dateOfOldestProjectInStatusRegistered, isAsc);
                case 'period':
                    return sortCompare(a.periodFrom, b.periodFrom, isAsc);
                default:
                    return 0;
            }
        });
    }

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    //#endregion
}
